import type { DeviceModels, DeviceTypeConfig } from '@eppendorf/app-configs';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { fetcher } from '$shared/fetch';
import { createAppConfigPath } from '$shared/utils/api-paths';

import {
  DeviceCategoriesListQuery,
  DeviceCategoriesListResponse,
} from './app-config.types';

export const appConfigQueryKey = 'application-configuration';

export const createGetDeviceTypeConfigsQuery = () => ({
  queryKey: [appConfigQueryKey, 'device-type-configs'],
  queryFn: async () =>
    fetcher.get<DeviceTypeConfig<DeviceModels>[]>(createAppConfigPath('device-types')),
  placeholderData: keepPreviousData,
  staleTime: Infinity,
  cacheTime: Infinity,
});
export const useGetDeviceTypeConfigs = () => useQuery(createGetDeviceTypeConfigsQuery());

const createDeviceCategoriesListRequest = (query?: DeviceCategoriesListQuery) => {
  if (query?.models) {
    const models = [
      ...new Set(query.models.map((model) => model.trim()).filter(Boolean)),
    ].sort();

    if (!models.length) return undefined;

    return {
      queryKey: ['models', ...models],
      searchParams: new URLSearchParams({ models: models.join(',') }),
    };
  }

  const deviceType = query?.deviceType.trim();

  if (!deviceType) return undefined;

  return {
    queryKey: ['deviceType', deviceType],
    searchParams: new URLSearchParams({ deviceType }),
  };
};

export const createGetDeviceCategoriesListQuery = (query?: DeviceCategoriesListQuery) => {
  const request = createDeviceCategoriesListRequest(query);

  if (!request) {
    return {
      queryKey: [appConfigQueryKey, 'device-categories-list', 'disabled'],
      queryFn: async (): Promise<DeviceCategoriesListResponse> => ({ data: [] }),
      enabled: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    };
  }

  return {
    queryKey: [appConfigQueryKey, 'device-categories-list', ...request.queryKey],
    queryFn: async () =>
      fetcher.get<DeviceCategoriesListResponse>(
        `${createAppConfigPath('device-categories-list')}?${request.searchParams}`,
      ),
    placeholderData: keepPreviousData,
    staleTime: Infinity,
    cacheTime: Infinity,
  };
};

export const useGetDeviceCategoriesList = (query?: DeviceCategoriesListQuery) =>
  useQuery(createGetDeviceCategoriesListQuery(query));

export const createGetFeatureToggleQuery = () => ({
  queryKey: [appConfigQueryKey, 'feature-toggles'],
  queryFn: async () => {
    const response = await fetcher.get<{ data: string[] }>(
      createAppConfigPath('feature-toggles'),
    );
    return response.data;
  },
  placeholderData: keepPreviousData,
  staleTime: Infinity,
  cacheTime: Infinity,
});

export const useGetFeatureToggle = () => useQuery(createGetFeatureToggleQuery());
